import { Box, Container, Stack, Typography } from '@mui/material';

import React, { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { debounce } from 'lodash';

import { addRelationshipMapping } from 'common/api';
import { ReactComponent as CloseIcon } from 'common/assets/images/close.svg';
import { Loader } from 'common/components/Loader/Loader';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	APP_GENERAL_ERROR_MSG,
	PERMISSION_DENIED_ERROR_MSG,
} from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { getCoveredProductData } from '../../api/index';
import { ICoveredProductRes } from '../../api/type-def';

import styles from '../Overview.module.scss';

import ButtonComp from '../../../../../common/components/Button/ButtonComp';
import ModalEle from '../../../../../common/components/Modal/ModalEle';
import SearchSelectDropdownEle from '../../../../../common/components/SearchableSelectDropdown/SearchSelectDropdown';
import { ENTITY_STATUS } from '../../../../../common/constants/TaxonomyConstants';

interface AddRelationMappingProps {
	onCloseModal: () => void;
	onAdd?: () => void;
	entityId: string;
	entityType: string;
}

interface Option {
	id: string;
	name: string;
}

const AddCoveredProduct = (props: AddRelationMappingProps) => {
	const { onCloseModal, onAdd, entityId, entityType } = props;
	const [selectedValue, setSelectedValue] = useState('');
	const [selectedId, setSelectedId] = useState('');
	const [searchText, setSearchText] = useState('');
	const [isAddDisabled, setIsAddDisabled] = useState(true);

	const [menuItems, setMenuItems] = useState<Option[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const riskRefTaxonomySourceFiltersKey = useSelector(
		(state: RootState) => state.taxonomy.riskRefTaxonomySourceFiltersKey,
	);
	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const { ADDED } = ENTITY_STATUS;
	const dispatch = useDispatch();

	const changeSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setSearchText(value);
	};

	const { OK, CREATED, FORBIDDEN } = HTTP_STATUS_CODES;

	const debouncedSearchHandler = debounce(changeSearchInput, 1000);

	const fetchCoveredProductData = async (searchText?: string) => {
		const res = await getCoveredProductData(
			searchText,
			riskRefTaxonomySourceFiltersKey,
		);
		if (res?.statusCode === OK) {
			const data: Option[] = [];
			res?.responseBody?.forEach((el: ICoveredProductRes) =>
				data.push({
					id: el.id,
					name: el.productName,
				}),
			);
			setMenuItems(data);
		}
	};

	useEffect(() => {
		if (selectedValue) {
			setIsAddDisabled(false);
		}
	}, [selectedValue]);

	useEffect(() => {
		fetchCoveredProductData();
	}, [entityType]);

	useEffect(() => {
		fetchCoveredProductData(searchText.trim());
	}, [searchText]);

	const handleSelectChange = useCallback(
		(event: React.ChangeEvent<{ value: string }>) => {
			setSelectedValue(event.target.value);
		},
		[],
	);

	useEffect(() => {
		const selectedOption = menuItems.find(
			(option) => option.name === selectedValue,
		);
		if (selectedOption) {
			setSelectedId(selectedOption.id);
		}
	}, [selectedValue, menuItems, selectedId]);

	const cancelHandler = useCallback(() => {
		setSelectedValue('');
		onCloseModal();
	}, []);

	const addHandler = useCallback(async () => {
		if (entityId) {
			setIsLoading(true);
			const data = {
				parentObjectId: entityId,
				parentObjectType: entityType,
				impactObjectId: selectedId,
				impactObjectType: 'Product',
				loggedInUser: loggedInUser?.userId ?? '',
				reviewStatus: ADDED,
			};
			const response = await addRelationshipMapping([data]);
			if (response?.statusCode === CREATED) {
				dispatch(
					setToastMessage({
						message: 'Successfully associated  a Product',
						severity: 'success',
						isToastOpen: true,
					}),
				);
			} else if (response.statusCode === FORBIDDEN) {
				dispatch(
					setToastMessage({
						message: PERMISSION_DENIED_ERROR_MSG,
						severity: 'error',
						isToastOpen: true,
					}),
				);
			} else {
				dispatch(
					setToastMessage({
						message: response?.errorBody?.errorMessage || APP_GENERAL_ERROR_MSG,
						severity: 'error',
						isToastOpen: true,
					}),
				);
			}
			setIsLoading(false);
			if (onAdd) {
				onAdd();
			}
		}
		setSelectedValue('');
		onCloseModal();
	}, [selectedValue, selectedId]);

	return (
		<ModalEle open handleClose={onCloseModal}>
			<Container className={styles['add-container']}>
				<Stack direction="column">
					<Stack direction="row" justifyContent="space-between">
						<Typography className={styles['header-text']} data-testid="header">
							{toSentenceCase('Add New Covered Product')}
						</Typography>
						<CloseIcon
							fontSize="medium"
							onClick={onCloseModal}
							className={styles['add-closemodal']}
							data-testid="close-icon"
						/>
					</Stack>
					<Box className={styles['add-text-area']} data-testid="text-area">
						<Typography className={styles['add-text']}>
							Find and select covered products to add below.
						</Typography>
					</Box>
				</Stack>

				<div className={styles['add-dropdown-area']}>
					{isLoading ? (
						<Loader show={isLoading} />
					) : (
						<SearchSelectDropdownEle
							inputProps={{
								'data-testid': 'sub-type-option',
							}}
							label={toSentenceCase('Covered Product')}
							value={selectedValue}
							onChange={handleSelectChange}
							onInputChange={debouncedSearchHandler}
							options={menuItems}
							placeholder="Select"
							stylesClassName={styles['width-class']}
							noMarginLabel
						/>
					)}
				</div>
				<Box className={styles['btn-box']}>
					<Stack
						direction="row"
						justifyContent="flex-end"
						className={styles['btn-grp']}
					>
						<ButtonComp
							btnType="secondary"
							clickHandler={cancelHandler}
							data-testid="cancel-btn"
						>
							Cancel
						</ButtonComp>
						<ButtonComp
							btnType="primary"
							clickHandler={addHandler}
							disabled={isAddDisabled}
							data-testid="add-btn"
						>
							Add
						</ButtonComp>
					</Stack>
				</Box>
			</Container>
		</ModalEle>
	);
};

export default AddCoveredProduct;
